import React from "react";

import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import { uniqBy } from "lodash";
import { useTranslation } from "react-i18next";
import { useDealDefaultCurrencySelect } from "store";
import { AmortizationData, AmortizationRow, DebtSegment } from "store/types";
import { formatNumber } from "utils";
import { getFacilitySegment, getLastSegmentsByDealId, getWithdrawalSegments } from "utils/debt";

import { currencySign } from "../../constants";

type ParentDebtSettingsProps = {
  amortizationData: AmortizationData;
  onEditClick: () => void;
};

const today = new Date().toISOString().split("T")[0];

export const ParentDebtSettings: React.FC<ParentDebtSettingsProps> = React.memo(({ amortizationData, onEditClick }) => {
  const { t } = useTranslation("debt");
  const currency = useDealDefaultCurrencySelect();

  const formatAmount = (amount: number) => `${currencySign[currency!]}${formatNumber(amount)}`;

  const lastSegments = getLastSegmentsByDealId(amortizationData.segments);
  const withdrawals = getWithdrawalSegments(lastSegments).reduce((acc, segment) => acc + segment.loan_amount, 0);
  const facility = getFacilitySegment(lastSegments);
  const loanAmount = facility?.loan_amount || 0;
  const unfunded = loanAmount - withdrawals;

  const overallProperties = {
    [t("settings.loanAmount")]: formatAmount(loanAmount),
    [t("settings.unfundedAmount")]: formatAmount(unfunded),
    [t("settings.withdrawals")]: formatAmount(withdrawals)
  };

  const generateSubAssetData = (segments: DebtSegment[], row: AmortizationRow) => {
    const dealSegments = uniqBy(segments, "deal_id");
    return {
      [t("settings.amount")]: formatAmount(dealSegments.reduce((acc, segment) => acc + segment.loan_amount, 0)),
      [t("settings.outstandingPrincipal")]: formatAmount(row.starting_principal),
      [t("settings.accruedInterest")]: formatAmount(row.interest_payment)
    };
  };

  const todayRow = amortizationData.table.rows.find((row) => row.date === today);
  if (!todayRow) {
    return null;
  }

  const rows = todayRow.children ? todayRow.children : [todayRow];

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">{t("settings.tableTitle")}</Typography>
        <IconButton onClick={onEditClick}>
          <EditIcon />
        </IconButton>
      </Box>

      <Stack direction="row" spacing={4} sx={{ overflowX: "auto" }}>
        <Box flex={1}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            {t("settings.overallProperties")}
          </Typography>
          <List dense sx={{ p: 0 }}>
            {Object.entries(overallProperties).map(([key, value]) => (
              <ListItem key={key} sx={{ py: 0 }}>
                <ListItemText
                  sx={{ py: 0, m: 0, display: "flex", flexDirection: "row" }}
                  primary={key + ": "}
                  secondary={value}
                  slotProps={{
                    primary: {
                      color: "primary.main",
                      fontSize: "0.875rem",
                      display: "inline"
                    },
                    secondary: {
                      color: "text.primary",
                      display: "inline",
                      ml: 1
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {rows.map((row) => {
          const rowSegments = row.segmentIds.map(
            (id) => amortizationData.segments.find((segment) => segment.id === id)!
          );
          const subAssetData = generateSubAssetData(rowSegments, row);

          return (
            <Box flex={1} key={row.segmentIds.join(",")}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {row.sub_deal}
              </Typography>
              <List dense sx={{ p: 0 }}>
                {Object.entries(subAssetData).map(([key, value]) => (
                  <ListItem key={key} sx={{ py: 0 }}>
                    <ListItemText
                      sx={{ py: 0, m: 0, display: "flex", flexDirection: "row" }}
                      primary={key + ": "}
                      secondary={value}
                      slotProps={{
                        primary: {
                          color: "primary.main",
                          fontSize: "0.875rem",
                          display: "inline"
                        },
                        secondary: {
                          color: "text.primary",
                          display: "inline",
                          ml: 1
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </Stack>
    </>
  );
});
